/**
 * SupervisorMonitor — the lexsup landing page (lexsup plan §3.7).
 *
 * Two parts, mirroring PlatformHealth / RuntimeFleet conventions:
 *
 *   1. Health panel — the supervisor's up/down state, version, dry-run flag,
 *      and last-run summary (time + findings count). Sourced from the lexserver
 *      proxy `GET /api/platform/sup/health`. Grey "unavailable" when the
 *      supervisor isn't reachable (down, wedged, or LEXSUP_URL unset) — never a
 *      hard error, same posture as RuntimeFleet's feature-not-live state.
 *
 *   2. Launchpad — quick-launch links to the supervisor's own surfaces:
 *        - "Open OpenClaw dashboard" → the lexsup gateway UI at `baseUrl`
 *          (RETURNED by the health endpoint, never hard-coded), new tab.
 *        - "Supervisor op-logs" → the OperationalLogs page filtered to
 *          `service=lexiun-supervisor`.
 *        - "Runs & findings" → SupervisorOverview.
 *      A "Run audit now" button POSTs `/api/platform/sup/run`.
 */

import {
  Activity,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Eye,
  Gavel,
  HeartPulse,
  Loader2,
  PlayCircle,
  ScrollText,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePlatformApi } from "../hooks/usePlatformApi";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { PlatformApiError } from "../lib/platform-api";
import type { SupervisorHealthResponse } from "../types/supervisor";

function fmtTime(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function SupervisorMonitor() {
  const api = usePlatformApi();
  const [data, setData] = useState<SupervisorHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [runNotice, setRunNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getSupervisorHealth();
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load supervisor health");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30_000);
    return () => clearInterval(interval);
  }, [load]);

  const flashRunNotice = (msg: string) => {
    setRunNotice(msg);
    setTimeout(() => setRunNotice(null), 8000);
  };

  const handleRunAudit = useCallback(async () => {
    setRunning(true);
    try {
      const res = await api.runSupervisorAudit();
      const run = res.data;
      flashRunNotice(
        `Audit triggered — run ${run.run_id ?? "(no id)"} ${run.status ?? ""} with ${
          run.findings_count ?? 0
        } finding(s).`,
      );
      // Refresh the health panel so the last-run summary reflects this run.
      void load();
    } catch (e) {
      const msg =
        e instanceof PlatformApiError
          ? `${e.message} (${e.code})`
          : e instanceof Error
            ? e.message
            : "Run failed";
      flashRunNotice(`Could not run audit: ${msg}`);
    } finally {
      setRunning(false);
    }
  }, [api, load]);

  const supervisor = data?.supervisor;
  const reachable = supervisor?.reachable === true;
  const baseUrl = data?.baseUrl ?? null;
  const lastRun = data?.lastRun ?? null;

  return (
    <div className="space-y-6" data-testid="supervisor-monitor-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <HeartPulse className="h-5 w-5 text-indigo-500" /> Supervisor
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            The lexsup platform supervisor — health, last audit, and quick links to
            its own surfaces.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {loading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
          <button
            type="button"
            onClick={() => load()}
            disabled={loading}
            className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div
          className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
          data-testid="supervisor-monitor-error"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {runNotice && (
        <div
          className="rounded-md bg-indigo-50 p-3 text-sm text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300"
          data-testid="supervisor-run-notice"
        >
          {runNotice}
        </div>
      )}

      {/* Health panel */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-sm font-medium text-muted-foreground">
            <span>Supervisor health</span>
            <span
              className={
                reachable
                  ? "inline-flex items-center gap-1 rounded-md bg-green-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-green-700 dark:bg-green-900/40 dark:text-green-300"
                  : "inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400"
              }
              data-testid="supervisor-reachable-badge"
            >
              {reachable ? (
                <>
                  <CheckCircle className="h-3 w-3" /> Up
                </>
              ) : (
                <>
                  <XCircle className="h-3 w-3" /> Unavailable
                </>
              )}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!reachable ? (
            <div
              className="space-y-1 py-2 text-sm text-muted-foreground"
              data-testid="supervisor-unavailable"
            >
              <p>
                The supervisor isn&apos;t reachable right now.
                {baseUrl
                  ? " The lexsup container may be down, restarting, or wedged."
                  : " LEXSUP_URL is not configured on this lexserver, so the supervisor isn't wired in yet."}
              </p>
              <p className="text-xs">
                This panel updates automatically once the supervisor responds — no
                action needed.
              </p>
            </div>
          ) : (
            <div className="grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Service</span>
                <span className="font-mono text-xs">
                  {supervisor?.service ?? "—"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Role</span>
                <span className="font-mono text-xs">{supervisor?.role ?? "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Version</span>
                <span className="font-mono text-xs">
                  {supervisor?.version ?? "—"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Dry-run</span>
                <span className="text-xs">
                  {supervisor?.dryRun ? (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 font-medium uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                      on
                    </span>
                  ) : (
                    <span className="text-slate-400">off</span>
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Last run</span>
                <span className="text-xs">{fmtTime(lastRun?.started_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Last-run findings</span>
                <span className="text-xs tabular-nums">
                  {lastRun ? lastRun.findings_count : "—"}
                </span>
              </div>
            </div>
          )}

          {/* Run-audit action lives in the health card so it sits next to the
              state it acts on. Disabled when the supervisor is unreachable. */}
          <div className="mt-4 flex items-center gap-3 border-t border-slate-100 pt-3 dark:border-slate-800">
            <button
              type="button"
              onClick={handleRunAudit}
              disabled={!reachable || running}
              data-testid="supervisor-run-audit"
              title={
                reachable
                  ? "Trigger a fleet-audit run now"
                  : "The supervisor must be reachable to run an audit"
              }
              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {running ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <PlayCircle className="h-4 w-4" />
              )}
              {running ? "Running…" : "Run audit now"}
            </button>
            {lastRun?.status && (
              <span className="text-xs text-muted-foreground">
                Last run:{" "}
                <span
                  className={
                    lastRun.status === "completed"
                      ? "font-medium text-green-600 dark:text-green-400"
                      : "font-medium text-red-600 dark:text-red-400"
                  }
                >
                  {lastRun.status}
                </span>
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Launchpad */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Launchpad
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {/* OpenClaw dashboard — external link to the lexsup gateway UI.
                Sourced from baseUrl (health endpoint); disabled when unset. */}
            {baseUrl ? (
              <a
                href={baseUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="supervisor-launch-openclaw"
                className="flex items-start gap-3 rounded-md border border-slate-200 p-3 transition-colors hover:border-indigo-300 hover:bg-indigo-50/50 dark:border-slate-700 dark:hover:border-indigo-700 dark:hover:bg-indigo-900/20"
              >
                <Activity className="mt-0.5 h-5 w-5 shrink-0 text-indigo-500" />
                <div className="min-w-0">
                  <p className="flex items-center gap-1 text-sm font-medium text-foreground">
                    Open OpenClaw dashboard <ExternalLink className="h-3 w-3" />
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{baseUrl}</p>
                </div>
              </a>
            ) : (
              <div
                data-testid="supervisor-launch-openclaw-disabled"
                className="flex items-start gap-3 rounded-md border border-dashed border-slate-200 p-3 opacity-60 dark:border-slate-700"
                title="LEXSUP_URL is not configured — the supervisor's OpenClaw dashboard URL is unknown."
              >
                <Activity className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-muted-foreground">
                    OpenClaw dashboard
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Unavailable (LEXSUP_URL unset)
                  </p>
                </div>
              </div>
            )}

            {/* Supervisor op-logs — deep-link into OperationalLogs filtered to
                the supervisor's service slug. timeframe=48 (hours) so the link
                lands on a window that actually contains the DAILY audit's
                records (the page default of 1h would usually render empty). */}
            <Link
              to="/operational-logs?service=lexiun-supervisor&timeframe=48"
              data-testid="supervisor-launch-oplogs"
              className="flex items-start gap-3 rounded-md border border-slate-200 p-3 transition-colors hover:border-indigo-300 hover:bg-indigo-50/50 dark:border-slate-700 dark:hover:border-indigo-700 dark:hover:bg-indigo-900/20"
            >
              <ScrollText className="mt-0.5 h-5 w-5 shrink-0 text-indigo-500" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">Supervisor op-logs</p>
                <p className="text-xs text-muted-foreground">
                  service=lexiun-supervisor
                </p>
              </div>
            </Link>

            {/* Runs & findings — internal drill-down (SupervisorOverview). */}
            <Link
              to="/supervisor/overview"
              data-testid="supervisor-launch-overview"
              className="flex items-start gap-3 rounded-md border border-slate-200 p-3 transition-colors hover:border-indigo-300 hover:bg-indigo-50/50 dark:border-slate-700 dark:hover:border-indigo-700 dark:hover:bg-indigo-900/20"
            >
              <Eye className="mt-0.5 h-5 w-5 shrink-0 text-indigo-500" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">Runs &amp; findings</p>
                <p className="text-xs text-muted-foreground">
                  Recent audits and observations
                </p>
              </div>
            </Link>

            {/* Approvals — the human-in-the-loop decision queue (SupervisorApprovals). */}
            <Link
              to="/supervisor/approvals"
              data-testid="supervisor-launch-approvals"
              className="flex items-start gap-3 rounded-md border border-slate-200 p-3 transition-colors hover:border-indigo-300 hover:bg-indigo-50/50 dark:border-slate-700 dark:hover:border-indigo-700 dark:hover:bg-indigo-900/20"
            >
              <Gavel className="mt-0.5 h-5 w-5 shrink-0 text-indigo-500" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">Approvals</p>
                <p className="text-xs text-muted-foreground">
                  Review proposed actions
                </p>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
